/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { useStackNavigate } from '@/app/navigation/stack-links';
import { createField } from '@/components/forms/field.schema';
import { Badge } from '@/components/ui/Badge/badge';
import { deparmentCVA } from '@/components/ui/Badge/deparmentCVA';
import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { ticketsTableConfig } from '@/config/table-configs';
import { useProjects } from '@/features/projects/projects.hooks';
import { useWorks } from '@/features/works/works.hooks';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { useRegisterFilteredTickets } from '@/hooks/useTicketReportContext';
import { ShareContent } from '@/utils/share';
import { knownDepartments } from '@certhon/domain-models';
import { Ticket } from '@certhon/models';
import { ColumnDef, Table } from '@tanstack/react-table';
import { Landmark } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { omit } from 'remeda';
import z from 'zod';
import { BulkTicketEditForm } from './components/BulkTicketEditForm';
import { LabelsCell } from './components/LabelsCell';
import { TicketTitleCell } from './components/TicketTitleCell';
import { TicketUserStackCell } from './components/TicketUserStackCell';
import { ticketFields } from './ticket.fields';
import { getTicketActions } from './tickets.actions';
import { useCurrentUser } from '@/features/users/users.hooks';

const fields = omit(ticketFields, ['project_id' /* ,'title', 'labels' */]);

const emptyArray: any[] = [];

const defaultFilter = 'status is: "opened"';
const customColumns = [
  {
    id: 'title',
    header: 'Title',
    accessorKey: 'title',
    enableSorting: true,
    cell: ({ row }) => <TicketTitleCell ticket={row.original} />,
  },
  {
    id: 'users',
    header: 'Users',
    enableSorting: false,
    cell: ({ row }) => <TicketUserStackCell ticket={row.original} />,
  },
  {
    id: 'labels',
    header: 'Labels',
    enableSorting: false,
    cell: ({ row }) => <LabelsCell ticket={row.original} />,
  },
] as ColumnDef<Ticket>[];

const excludeColumns = ['department'];
export function TicketTable({
  uriPrefix = '',
  data,
  isLoading,
  projectLevel = false,
  projectId,
  tableId = 'tickets',
  defaultConfig = ticketsTableConfig,
}: {
  uriPrefix?: string;
  projectLevel?: boolean;
  projectId?: number;
  tableId?: string;
  defaultConfig?: typeof ticketsTableConfig;
} & DataTableConsumer<Ticket>) {
  const crossProject = projectId === undefined;
  const currentUser = useCurrentUser();

  const { filterValue, setFilterValue } = usePanelFilterState({
    prefix: 't.',
    defaultFilter,
  });

  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState({ prefix: 't.' });

  // Clear selection when filter changes (context change)
  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  // Get row actions for tickets
  const rowActions = useMemo(
    () => getTicketActions(currentUser?.recnum ?? null),
    [currentUser],
  );

  /**
   * FIXME: This is a hack to be able to filter on department
   *  This is difficult because its an indirect relationship (via werk)
   *  this should either
   *   - be supported natively
   *  OR
   *   - be made simple by redudantly querying (joining) department with ticket on the schema level.
   *
   * We avoid the latter because of parallel environments (current,next)
   */
  const works = useWorks(projectId);
  const worksDepMapRef = useRef<Record<number, string>>(
    Object.fromEntries(
      works.data?.map((work) => [work.recnum, work.afd]) || [],
    ),
  );
  useEffect(() => {
    Object.assign(
      worksDepMapRef.current,
      Object.fromEntries(
        works.data?.map((work) => [work.recnum, work.afd]) || [],
      ),
    );
  }, [works.data]);

  // Fetch projects for cross-project views so the virtual `project`/`customer`
  // foreignKey fields in ticket.fields.ts can resolve via context.
  const projectsQuery = useProjects(
    crossProject ? undefined : { enabled: false },
  );

  // Lookup maps passed via context — change identity when query data lands,
  // which makes useFilterableTable rebuild tokens/columns/filterFn so virtual
  // FK cells re-render with resolved IDs.
  const workToProject = useMemo<Record<number, number>>(() => {
    const m: Record<number, number> = {};
    works.data?.forEach((w) => {
      m[w.recnum] = Number(w.project_id);
    });
    return m;
  }, [works.data]);

  const projectToCustomer = useMemo<Record<number, number>>(() => {
    const m: Record<number, number> = {};
    projectsQuery.data?.forEach((p) => {
      m[p.recnum] = p.klant_id;
    });
    return m;
  }, [projectsQuery.data]);

  const extraFields = useMemo(() => {
    return {
      ...fields,
      department: createField(z.string().nullable(), {
        label: 'Department',
        fieldType: 'select',
        options: knownDepartments
          .filter((dep) => dep.show)
          .map((dep) => ({
            label: dep.name,
            value: dep.char,
          })),
        filterConfig: { icon: Landmark },
        getVirtualValue(entity) {
          return worksDepMapRef.current[entity.werk_id] || null;
        },
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksDepMapRef.current]);

  // TODO: Migrate `department` to the virtual-FK pattern used by the
  // `project` and `customer` fields in ticket.fields.ts: declare it there
  // with `fieldType: 'select'` (or a 'department' entity config) and a
  // `getVirtualValue(t, ctx)` that reads `ctx.workToDepartment`. Push the
  // lookup map through context like `workToProject` below. Once that lands,
  // this whole custom column block + the `excludeColumns` entry can go.
  const extraCustomColumns = useMemo<ColumnDef<Ticket>[]>(
    () => [
      ...customColumns,
      {
        id: 'department',
        header: 'Department',
        enableSorting: true,
        enableMultiSort: true,
        accessorFn: (ticket: Ticket) =>
          worksDepMapRef.current[ticket.werk_id!] || ' ',
        cell: ({ row }) => {
          const char =
            (row.original.werk_id &&
              worksDepMapRef.current[row.original.werk_id]) ||
            null;
          if (!char) return '-';
          const dep = knownDepartments.find((d) => d.char === char);
          return (
            <Badge
              key={row.id}
              variant="outline"
              className={deparmentCVA({ variant: (dep?.char || '_') as '_' })}
            >
              {dep?.name || char}
            </Badge>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [worksDepMapRef.current],
  );

  // Use schema-driven filter system
  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<Ticket>({
    fields: extraFields,
    data: data || emptyArray,
    filterValue,
    customColumns: extraCustomColumns,
    excludeColumns,
    context: { projectId, workToProject, projectToCustomer },
  });

  // Register filtered tickets for PDF report generation (if context available)
  // We use onSortedDataChange callback to get the correctly sorted data
  const registerFilteredTickets = useRegisterFilteredTickets();
  const handleSortedDataChange = useCallback(
    (sortedData: Ticket[]) => {
      registerFilteredTickets(sortedData, filterValue, tokenDefinitions);
    },
    [registerFilteredTickets, filterValue, tokenDefinitions],
  );

  const completeDefaultConfig = useMemo(
    () => completeConfig(defaultConfig, columns),
    [columns, defaultConfig],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    tableId,
    completeDefaultConfig,
  );

  const renderBulkEdit = useCallback(
    (table: Table<Ticket>, close: () => void) => (
      <BulkTicketEditForm
        selectedTickets={table
          .getSelectedRowModel()
          .rows.map((row) => row.original)}
        onClose={() => {
          close();
        }}
      />
    ),
    [],
  );

  const navigate = useStackNavigate();

  const createUriPath = useCallback(
    (t: Ticket): string => {
      if (projectLevel) {
        return t.werk_id
          ? [uriPrefix, '/works/', t.werk_id, '/tickets/', t.id].join('')
          : [uriPrefix, '/tickets/', t.id].join('');
      }
      return [uriPrefix, '/', t.id].join('');
    },
    [uriPrefix, projectLevel],
  );

  const createShareContent = useCallback(
    (ticket: Ticket, url: string): ShareContent => ({
      title: `Ticket #${ticket.number}: ${ticket.title}`,
      text: ticket.description || `Check out this ticket: #${ticket.number}`,
      url,
    }),
    [],
  );

  const addPath = projectLevel
    ? `${uriPrefix}/tickets/new`
    : `${uriPrefix}/new`;

  const handleNew = useCallback(() => {
    navigate({ to: addPath });
  }, [navigate, addPath]);

  return (
    <DataTable<Ticket, any>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={createUriPath}
      createShareContent={createShareContent}
      renderBulkEdit={renderBulkEdit}
      onNew={handleNew}
      filterValue={filterValue}
      defaultFilter={defaultFilter}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="tickets"
      addPath={addPath}
      actions={rowActions}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      getRowId={(t) => t.id}
      onSortedDataChange={handleSortedDataChange}
    />
  );
}
